import React, { useMemo, useState } from 'react';
import { useOnTimeLiveData } from '../hooks/useOnTimeLiveData';
import { formatDuration } from '../lib/formatters';

const currency = (value) => `R ${Number(value || 0).toLocaleString('en-ZA')}`;

const darkSelectStyle = {
  backgroundColor: '#071426',
  color: '#e8fbff',
  border: '1px solid rgba(34, 211, 238, 0.28)',
  boxShadow: '0 0 0 1px rgba(14, 165, 233, 0.08), inset 0 0 24px rgba(8, 47, 73, 0.35)',
};

const darkOptionStyle = {
  backgroundColor: '#071426',
  color: '#e8fbff',
};

function asDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getPeriodRange(period) {
  const now = new Date();
  const today = startOfDay(now);
  if (period === 'today') return { start: today, end: new Date(today.getTime() + 86400000), label: 'Today' };
  if (period === 'yesterday') {
    const start = new Date(today.getTime() - 86400000);
    return { start, end: today, label: 'Yesterday' };
  }
  if (period === 'last7') return { start: new Date(today.getTime() - 6 * 86400000), end: new Date(today.getTime() + 86400000), label: 'Last 7 days' };
  if (period === 'mtd') return { start: new Date(today.getFullYear(), today.getMonth(), 1), end: new Date(today.getTime() + 86400000), label: 'Month to date' };
  return { start: null, end: null, label: 'All time' };
}

function inRange(date, range) {
  if (!date) return false;
  const time = date.getTime();
  if (range.start && time < range.start.getTime()) return false;
  if (range.end && time >= range.end.getTime()) return false;
  return true;
}

function isCancelled(order) {
  const text = `${order.stage || ''} ${order.status || ''} ${order.raw?.status || ''}`.toLowerCase();
  return text.includes('cancel');
}

function isDelivered(order) {
  const text = `${order.stage || ''} ${order.status || ''} ${order.raw?.status || ''}`.toLowerCase();
  return text.includes('delivered') || text.includes('completed');
}

function isRerouted(order) {
  const text = `${order.raw?.archiveReason || ''} ${order.raw?.reason || ''} ${order.raw?.lastEventType || ''} ${order.raw?.status || ''}`.toLowerCase();
  return Boolean(order.raw?.reroutedFrom || order.raw?.reroutedTo || order.raw?.archiveType === 'rerouted' || text.includes('rerout'));
}

function eventTime(order, keywords) {
  const events = Array.isArray(order.events) ? order.events : [];
  const found = events.find((event) => {
    const label = `${event.type || ''} ${event.label || ''} ${event.title || ''} ${event.message || ''}`.toLowerCase();
    return keywords.some((word) => label.includes(word));
  });
  return asDate(found?.createdAt);
}

function minutesBetween(a, b) {
  const start = asDate(a);
  const end = asDate(b);
  if (!start || !end) return 0;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
}

function averageMinutes(values) {
  const filtered = values.map(Number).filter((value) => Number.isFinite(value) && value > 0);
  if (!filtered.length) return '0m';
  return formatDuration(Math.round(filtered.reduce((sum, value) => sum + value, 0) / filtered.length) * 60);
}

function totalForOrder(order) {
  const raw = order.raw || {};
  const pricing = raw.pricing || {};
  return Number(pricing.totalToCollect ?? pricing.total ?? order.total ?? raw.total ?? 0);
}

function revenueForOrder(order) {
  const payment = `${order.paymentMethod || order.raw?.paymentMethod || ''}`.toLowerCase();
  if (payment.includes('cash')) return totalForOrder(order);
  if (payment.includes('cod')) return totalForOrder(order);
  return totalForOrder(order);
}

function getSite(order) {
  return order.store || order.raw?.siteName || order.raw?.storeName || order.raw?.store || 'Unassigned site';
}

function getDriver(order) {
  return order.assignedDriverName || order.assignedDriver || order.raw?.assignment?.driverName || order.raw?.driverName || 'Unassigned driver';
}

function getOnline(order) {
  return order.assignedOnline || order.sentByName || order.createdByName || order.raw?.createdBy?.name || 'Unassigned online';
}

function timeLabel(date) {
  const d = asDate(date);
  return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
}

function ReportSelect({ value, onChange, children, label }) {
  return (
    <label className="min-w-[150px] text-xs uppercase tracking-[0.25em] text-cyan-200/70">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-2xl px-4 py-3 text-sm font-semibold normal-case tracking-normal outline-none"
        style={darkSelectStyle}
      >
        {children}
      </select>
    </label>
  );
}

function Metric({ label, value, hint }) {
  return (
    <div className="rounded-3xl border border-cyan-300/10 bg-[#071426]/80 p-5 shadow-[0_0_40px_rgba(8,47,73,.18)]">
      <div className="text-xs font-bold uppercase tracking-[0.35em] text-cyan-200">{label}</div>
      <div className="mt-5 text-3xl font-black text-white">{value}</div>
      {hint ? <div className="mt-2 text-sm text-slate-400">{hint}</div> : null}
    </div>
  );
}

export default function OnTimeReportsPage() {
  const { orders } = useOnTimeLiveData();
  const [period, setPeriod] = useState('today');
  const [site, setSite] = useState('all');
  const [reportType, setReportType] = useState('all');

  const range = useMemo(() => getPeriodRange(period), [period]);
  const sites = useMemo(() => Array.from(new Set(orders.map(getSite).filter(Boolean))).sort(), [orders]);

  const periodOrders = useMemo(() => {
    return orders.filter((order) => {
      const date = asDate(order.updatedAt) || asDate(order.placedAt) || asDate(order.createdAt);
      if (!inRange(date, range)) return false;
      if (site !== 'all' && getSite(order) !== site) return false;
      return true;
    });
  }, [orders, range, site]);

  const totals = useMemo(() => {
    const delivered = periodOrders.filter(isDelivered);
    const cancelled = periodOrders.filter(isCancelled);
    return {
      orders: periodOrders.length,
      delivered: delivered.length,
      cancelled: cancelled.length,
      rerouted: periodOrders.filter(isRerouted).length,
      issues: periodOrders.filter((order) => order.issueFlag || order.flags?.requiresCustomerContact || order.flags?.hasStockIssue).length,
      revenue: delivered.reduce((sum, order) => sum + revenueForOrder(order), 0),
      avgCompletion: averageMinutes(delivered.map((order) => minutesBetween(order.placedAt, order.updatedAt || eventTime(order, ['delivered', 'completed'])))),
    };
  }, [periodOrders]);

  const siteRows = useMemo(() => {
    const map = {};
    periodOrders.forEach((order) => {
      const key = getSite(order);
      if (!map[key]) map[key] = { name: key, orders: [], waiting: [], packing: [], ready: [] };
      map[key].orders.push(order);
      map[key].waiting.push(minutesBetween(order.placedAt, eventTime(order, ['packing started', 'packed', 'approval approved']) || order.updatedAt));
      map[key].packing.push(minutesBetween(eventTime(order, ['packing started']), eventTime(order, ['packed', 'packing proof'])));
      map[key].ready.push(minutesBetween(eventTime(order, ['packed', 'ready']), eventTime(order, ['collected', 'handover'])));
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name)).map((row) => ({
      site: row.name,
      orders: row.orders.length,
      delivered: row.orders.filter(isDelivered).length,
      cancelled: row.orders.filter(isCancelled).length,
      rerouted: row.orders.filter(isRerouted).length,
      issues: row.orders.filter((order) => order.issueFlag || order.flags?.requiresCustomerContact || order.flags?.hasStockIssue).length,
      waiting: averageMinutes(row.waiting),
      packing: averageMinutes(row.packing),
      ready: averageMinutes(row.ready),
      cod: row.orders.filter((order) => `${order.paymentMethod}`.toLowerCase().includes('cash')).reduce((sum, order) => sum + totalForOrder(order), 0),
      online: row.orders.filter((order) => !`${order.paymentMethod}`.toLowerCase().includes('cash')).reduce((sum, order) => sum + totalForOrder(order), 0),
    }));
  }, [periodOrders]);

  const driverRows = useMemo(() => {
    const map = {};
    periodOrders.forEach((order) => {
      const key = getDriver(order);
      if (!map[key]) map[key] = { name: key, orders: [] };
      map[key].orders.push(order);
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name)).map((row) => ({
      driver: row.name,
      assigned: row.orders.length,
      delivered: row.orders.filter(isDelivered).length,
      cancelled: row.orders.filter(isCancelled).length,
      avgRun: averageMinutes(row.orders.map((order) => minutesBetween(eventTime(order, ['out for delivery', 'start run']), eventTime(order, ['delivered'])))),
      revenue: row.orders.filter(isDelivered).reduce((sum, order) => sum + totalForOrder(order), 0),
    }));
  }, [periodOrders]);

  const auditRows = useMemo(() => {
    return periodOrders.flatMap((order) => {
      const events = Array.isArray(order.events) ? order.events : [];
      if (!events.length) {
        return [{ order, actor: getOnline(order), action: order.stage || 'Order activity', time: order.updatedAt || order.placedAt }];
      }
      return events.map((event) => ({
        order,
        actor: event.actorName || event.userName || event.createdByName || getOnline(order),
        action: event.label || event.title || event.message || event.type || 'Order activity',
        time: event.createdAt,
      }));
    }).sort((a, b) => (asDate(b.time)?.getTime?.() || 0) - (asDate(a.time)?.getTime?.() || 0)).slice(0, 80);
  }, [periodOrders]);

  return (
    <div className="relative space-y-7 pb-10">
      <section className="rounded-[2rem] border border-white/10 bg-[#061222]/80 p-6 shadow-[0_0_80px_rgba(14,165,233,.08)]">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.4em] text-cyan-300">Link Reports</div>
            <h2 className="mt-4 text-4xl font-light text-white">Operational reporting</h2>
            <p className="mt-3 max-w-5xl text-sm leading-6 text-slate-400">
              Site, driver, and Control Tower audit reporting built from the same live order records used by Intake, Site, Driver, Control Tower, and Archives.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <ReportSelect label="Report type" value={reportType} onChange={setReportType}>
              <option style={darkOptionStyle} value="all">All</option>
              <option style={darkOptionStyle} value="sites">Sites</option>
              <option style={darkOptionStyle} value="drivers">Drivers</option>
              <option style={darkOptionStyle} value="audit">Control Tower audit</option>
            </ReportSelect>
            <ReportSelect label="Period" value={period} onChange={setPeriod}>
              <option style={darkOptionStyle} value="today">Today</option>
              <option style={darkOptionStyle} value="yesterday">Yesterday</option>
              <option style={darkOptionStyle} value="last7">Last 7 days</option>
              <option style={darkOptionStyle} value="mtd">Month to date</option>
              <option style={darkOptionStyle} value="all">All time</option>
            </ReportSelect>
            <ReportSelect label="Site" value={site} onChange={setSite}>
              <option style={darkOptionStyle} value="all">All Sites</option>
              {sites.map((name) => <option key={name} style={darkOptionStyle} value={name}>{name}</option>)}
            </ReportSelect>
          </div>
        </div>

        <div className="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <Metric label="Orders" value={totals.orders} hint={range.label} />
          <Metric label="Delivered" value={totals.delivered} hint="Completed by driver" />
          <Metric label="Cancelled" value={totals.cancelled} hint="Customer, site, or driver cancelled" />
          <Metric label="Revenue" value={currency(totals.revenue)} hint="Delivered + live active value" />
          <Metric label="Avg completion" value={totals.avgCompletion} hint="Placed to delivered" />
        </div>
      </section>

      {(reportType === 'all' || reportType === 'sites') ? (
        <section className="rounded-[2rem] border border-white/10 bg-[#061222]/80 p-6">
          <div className="text-xs font-bold uppercase tracking-[0.35em] text-cyan-300">Site Performance</div>
          <h3 className="mt-3 text-2xl font-light text-white">Site work, fulfilment, and payment totals</h3>
          <div className="mt-6 overflow-x-auto rounded-3xl border border-cyan-200/10">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-white/5 text-slate-400">
                <tr>
                  <th className="px-4 py-3">Site</th><th className="px-4 py-3">Orders</th><th className="px-4 py-3">Delivered</th><th className="px-4 py-3">Cancelled</th><th className="px-4 py-3">Rerouted</th><th className="px-4 py-3">Issues</th><th className="px-4 py-3">Avg waiting</th><th className="px-4 py-3">Avg packing</th><th className="px-4 py-3">Avg ready</th><th className="px-4 py-3">COD</th><th className="px-4 py-3">Card/Online</th>
                </tr>
              </thead>
              <tbody>
                {siteRows.map((row) => (
                  <tr key={row.site} className="border-t border-white/5">
                    <td className="px-4 py-3 font-bold text-white">{row.site}</td><td className="px-4 py-3 text-slate-300">{row.orders}</td><td className="px-4 py-3 text-emerald-200">{row.delivered}</td><td className="px-4 py-3 text-rose-200">{row.cancelled}</td><td className="px-4 py-3 text-amber-200">{row.rerouted}</td><td className="px-4 py-3 text-cyan-100">{row.issues}</td><td className="px-4 py-3 text-slate-300">{row.waiting}</td><td className="px-4 py-3 text-slate-300">{row.packing}</td><td className="px-4 py-3 text-slate-300">{row.ready}</td><td className="px-4 py-3 text-white">{currency(row.cod)}</td><td className="px-4 py-3 text-white">{currency(row.online)}</td>
                  </tr>
                ))}
                {!siteRows.length ? <tr><td className="px-4 py-5 text-slate-400" colSpan={11}>No site records found for this filter.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {(reportType === 'all' || reportType === 'drivers') ? (
        <section className="rounded-[2rem] border border-white/10 bg-[#061222]/80 p-6">
          <div className="text-xs font-bold uppercase tracking-[0.35em] text-cyan-300">Driver Performance</div>
          <h3 className="mt-3 text-2xl font-light text-white">Driver movement, delivery totals, and run averages</h3>
          <div className="mt-6 overflow-x-auto rounded-3xl border border-cyan-200/10">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-white/5 text-slate-400"><tr><th className="px-4 py-3">Driver</th><th className="px-4 py-3">Assigned</th><th className="px-4 py-3">Delivered</th><th className="px-4 py-3">Cancelled</th><th className="px-4 py-3">Avg run</th><th className="px-4 py-3">Revenue</th></tr></thead>
              <tbody>
                {driverRows.map((row) => <tr key={row.driver} className="border-t border-white/5"><td className="px-4 py-3 font-bold text-white">{row.driver}</td><td className="px-4 py-3 text-slate-300">{row.assigned}</td><td className="px-4 py-3 text-emerald-200">{row.delivered}</td><td className="px-4 py-3 text-rose-200">{row.cancelled}</td><td className="px-4 py-3 text-slate-300">{row.avgRun}</td><td className="px-4 py-3 text-white">{currency(row.revenue)}</td></tr>)}
                {!driverRows.length ? <tr><td className="px-4 py-5 text-slate-400" colSpan={6}>No driver records found for this filter.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {(reportType === 'all' || reportType === 'audit') ? (
        <section className="rounded-[2rem] border border-white/10 bg-[#061222]/80 p-6">
          <div className="text-xs font-bold uppercase tracking-[0.35em] text-cyan-300">Control Tower Audit</div>
          <h3 className="mt-3 text-2xl font-light text-white">Actions, owners, and timeline signals</h3>
          <div className="mt-6 overflow-x-auto rounded-3xl border border-cyan-200/10">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-white/5 text-slate-400"><tr><th className="px-4 py-3">Time</th><th className="px-4 py-3">Order</th><th className="px-4 py-3">Site</th><th className="px-4 py-3">Actor</th><th className="px-4 py-3">Action</th></tr></thead>
              <tbody>
                {auditRows.map((row, index) => <tr key={`${row.order.id}-${index}`} className="border-t border-white/5"><td className="px-4 py-3 text-slate-300">{timeLabel(row.time)}</td><td className="px-4 py-3 font-bold text-white">#{row.order.orderNumber}</td><td className="px-4 py-3 text-slate-300">{getSite(row.order)}</td><td className="px-4 py-3 text-cyan-100">{row.actor}</td><td className="px-4 py-3 text-slate-200">{row.action}</td></tr>)}
                {!auditRows.length ? <tr><td className="px-4 py-5 text-slate-400" colSpan={5}>No audit records found for this filter.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
